/**
 * Provider composition. Order matters and is not arbitrary:
 *
 *   ErrorBoundary  outermost, so a crash in any provider is still caught
 *   Language       before everything, so even the auth screens are translated
 *   Query          before Auth, because Auth's restore uses the client
 *   Auth           before the router, because the guards read it
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { useMemo } from 'react';

import { AppRoutes } from './routes';
import { ErrorBoundary } from './ErrorBoundary';
import { createQueryClient } from './query';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { LanguageProvider } from '@/features/language/LanguageProvider';
import { FlagProvider } from '@/features/flags/FlagProvider';
import { OfflineBanner } from '@/components/layout/OfflineBanner';
import { PwaPrompts } from '@/components/layout/PwaPrompts';
import { PinGateProvider } from '@/features/parent/usePinGate';

export function App() {
  // Created once, not per render: a new client would drop every cache entry.
  const queryClient = useMemo(createQueryClient, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* Inside AuthProvider, because the gate borrows its session; and
                outside the router, because a parent action asked for on one
                screen must not be cancelled by navigating to another. */}
            <PinGateProvider>
              <FlagProvider>
                <BrowserRouter>
                  <OfflineBanner />
                  <AppRoutes />
                  <PwaPrompts />
                </BrowserRouter>
              </FlagProvider>
            </PinGateProvider>
          </AuthProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
